import DOMPurify from 'dompurify';

const SAFE_IMAGE_DATA_PATTERN = /^data:image\/(?:png|gif|jpeg|jpg|webp);/i;
const BLOCKED_PROTOCOL_PATTERN = /^(?:javascript|data|vbscript|file):/i;

const cleanValue = (value) => String(value ?? '')
  .trim()
  .split('')
  .filter((char) => {
    const code = char.charCodeAt(0);
    return code > 31 && code !== 127;
  })
  .join('');

export const sanitizeNavigationTarget = (value) => {
  const cleaned = cleanValue(value);
  if (!cleaned) return null;

  const compact = cleaned.replace(/\s+/g, '');
  if (BLOCKED_PROTOCOL_PATTERN.test(compact)) return null;

  if (/^(https?:\/\/|mailto:|tel:)/i.test(cleaned)) return cleaned;
  if (/^(\/|#|\?|\.\/|\.\.\/)/.test(cleaned)) return cleaned;

  return null;
};

export const sanitizeAssetUrl = (value) => {
  const cleaned = cleanValue(value);
  if (!cleaned) return null;

  const compact = cleaned.replace(/\s+/g, '');
  if (BLOCKED_PROTOCOL_PATTERN.test(compact) && !SAFE_IMAGE_DATA_PATTERN.test(compact)) {
    return null;
  }

  if (SAFE_IMAGE_DATA_PATTERN.test(cleaned)) return cleaned;
  if (/^(https?:\/\/|\/|\?|\.\/|\.\.\/)/i.test(cleaned)) return cleaned;

  return null;
};

export const sanitizeHtmlContent = (html) => {
  if (typeof html !== 'string' || !html.trim()) return '';

  const sanitized = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'option', 'link', 'meta', 'base'],
    FORBID_ATTR: ['srcdoc'],
    ALLOW_UNKNOWN_PROTOCOLS: false
  });

  if (typeof window === 'undefined' || !sanitized) {
    return sanitized;
  }

  const parser = new DOMParser();
  const documentFragment = parser.parseFromString(sanitized, 'text/html');

  documentFragment.querySelectorAll('a[href]').forEach((link) => {
    const safeHref = sanitizeNavigationTarget(link.getAttribute('href'));

    if (!safeHref) {
      link.removeAttribute('href');
      link.removeAttribute('target');
      link.removeAttribute('rel');
      return;
    }

    link.setAttribute('href', safeHref);

    if (/^https?:\/\//i.test(safeHref)) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    }
  });

  documentFragment.querySelectorAll('[src]').forEach((element) => {
    const safeSrc = sanitizeAssetUrl(element.getAttribute('src'));

    if (!safeSrc) {
      element.remove();
      return;
    }

    element.setAttribute('src', safeSrc);
  });

  documentFragment.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      if (/^on/i.test(attribute.name)) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return documentFragment.body.innerHTML;
};
